import { Subscription } from 'generated/api';
import React, { MouseEventHandler, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import SubscriptionUtils from 'services/SubscriptionUtils';
import './style.scss';

type Props = {
  subscriptions: Subscription[];
  showHistoricalData?: boolean;
};

type PeriodData = {
  year: number;
  month: number;
  subs: Subscription[];
  total: number;
  percentage: number;
  cohort: {
    year: number;
    month: number;
    subs: Subscription[];
  };
};

const colorLerp = _.memoize((value: number, min: number, max: number) => {
  min -= 10;
  const steps = 13;
  const step = (max - min) / steps;
  const currStep = Math.floor((value - min) / step);
  let fraction = currStep / steps;

  return {
    background: `rgba(${0x26}, ${0x93}, ${0xd1}, ${fraction})`,
    color: fraction <= 0.5 ? 'black' : 'white',
  };
});

const monthToString = _.memoize((number: number) => {
  if (number > 9) {
    return number + '';
  }

  return '0' + number;
});

function RetentionGraph({ subscriptions, showHistoricalData }: Props) {
  const FORMAT = 'YYYY MM';
  const retentionGraph = useRef<HTMLDivElement>(null);
  const tooltip = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState({});

  const [data, minPercentage, maxPercentage] = useMemo(() => {
    const now = moment();

    const sorted_subs = _(subscriptions)
      .filter((it) => !SubscriptionUtils.isUnsubscribed(it) && it.created !== undefined)
      .sortBy('created')
      .groupBy((it) => moment.unix(it.created!).format(FORMAT))
      .value();

    const thisYear = now.year();
    const years = _.range(showHistoricalData ? 2018 : thisYear - 1, thisYear + 1);

    const periods = years.flatMap((year) => {
      return _.range(1, 13).map((month) => {
        const dateStr = year + ' ' + monthToString(month);
        const subs = sorted_subs[dateStr] ?? [];
        return { year, month, subs };
      });
    });

    let minPercentage = 100;
    let maxPercentage = 0;

    const data = _(periods)
      .map((cohort) => {
        const { year, month, subs } = cohort;
        const date = moment(year + ' ' + monthToString(month), FORMAT);

        if (date.clone().startOf('month').isAfter(now)) {
          return;
        }

        const subsOnPresent = subs.filter((it) => !it.canceled_at).length;
        return {
          year,
          month,
          now: { percentage: (subsOnPresent / subs.length) * 100, total: subsOnPresent },
          periods: _(_.range(13))
            .map((dt) => {
              const periodDate = date.clone().add(dt, 'months');
              if (periodDate.startOf('month').isAfter(now)) {
                return;
              }
              let subsThisMoment = subs;
              subsThisMoment = subs.filter(
                (it) =>
                  !it.canceled_at || moment.unix(it.canceled_at).isAfter(periodDate.endOf('month')),
              );
              const total = subsThisMoment.length;
              const percentage = (total / subs.length) * 100;
              if (percentage < minPercentage) minPercentage = percentage;
              if (percentage > maxPercentage) maxPercentage = percentage;

              return {
                year: periodDate.year(),
                month: periodDate.month() + 1,
                total,
                percentage,
                subs: subsThisMoment,
                cohort,
              } as PeriodData;
            })
            .compact()
            .sortBy(['year', 'month'])
            .value(),
        };
      })
      .compact()
      .value();

    return [data, minPercentage, maxPercentage] as const;
  }, [subscriptions, showHistoricalData]);

  useEffect(() => {
    const callback = (ev: MouseEvent) => {
      if (!retentionGraph.current) {
        return;
      }

      const { clientX, clientY } = ev;
      const graphRect = retentionGraph.current.getBoundingClientRect();

      if (
        clientX < graphRect.left ||
        clientX > graphRect.left + graphRect.width ||
        clientY < graphRect.top ||
        clientY > graphRect.top + graphRect.height
      ) {
        setTooltipData({});
      }
      const tooltipSize = tooltip.current?.getBoundingClientRect();
      if (tooltipSize) {
        const tooltipRight = clientX + tooltipSize.width;
        const tooltipBottom = clientY + tooltipSize.height;
        let dx = 0;
        let dy = 0;
        if (tooltipRight > window.innerWidth) {
          dx = tooltipRight - window.innerWidth;
        }
        if (tooltipBottom > window.innerHeight) {
          dy = tooltipBottom - window.innerHeight;
        }
        tooltip.current!.style.translate = `-${dx}px -${dy}px`;
      }

      setTooltipData((prev) => ({
        ...prev,
        left: clientX,
        top: clientY,
      }));
    };

    document.addEventListener('mousemove', callback);

    return () => {
      document.removeEventListener('mousemove', callback);
    };
  }, []);

  return (
    <div className="retention-graph" ref={retentionGraph}>
      <RetentionTooltip {...tooltipData} innerRef={tooltip} />
      <table className="w-full">
        <thead>
          <tr>
            <td></td>
            {_.range(13).map((idx) => (
              <td className="text-center">Month {idx}</td>
            ))}
            <td>Now</td>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            return (
              <tr key={index}>
                <td>
                  {moment(`${row.year} ${monthToString(row.month)}`, FORMAT).format('MMM YYYY')}
                </td>
                {row.periods.map((period) => {
                  const style = colorLerp(period.percentage ?? 0, minPercentage, maxPercentage);
                  return (
                    <td style={style} onMouseOver={() => setTooltipData(period)}>
                      {period.percentage.toFixed(0)}%
                    </td>
                  );
                })}
                <td
                  className="row-col now"
                  style={colorLerp(row.now.percentage, minPercentage, maxPercentage)}
                >
                  {row.now.percentage.toFixed(0)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default RetentionGraph;

function RetentionTooltip({
  subs,
  total,
  percentage,
  left,
  top,
  cohort,
  year,
  month,
  innerRef,
}: Partial<PeriodData> & {
  top?: number;
  left?: number;
  innerRef?: RefObject<HTMLDivElement>;
}) {
  if (!subs || !cohort) {
    return null;
  }
  const dateStr = moment(`${year} ${month}`, 'YYYY M').format('YYYY MMM');
  const cohortDateStr = moment(`${cohort.year} ${cohort.month}`, 'YYYY M').format('YYYY MMM');
  return (
    <div ref={innerRef} className="retention-tooltip" style={{ left, top }}>
      <div>
        {dateStr} (from: {cohortDateStr})
      </div>
      <div>
        {percentage!.toFixed(0)}% ({total}/{cohort.subs.length} subs)
      </div>
      <div>
        {/**@ts-expect-error*/}
        {100 - percentage!.toFixed(0)!}% ({cohort.subs.length - total!} subs) lost
      </div>
      {/*
      <p>Subs</p>
    <div className="grid" style={{ fontSize: '.8rem' }}>
      {
        _(subs)
          .filter(it => !!it.canceled_at)
          .sortBy('canceled_at')
          .map(sub => {
            return <div>
              {moment.unix(sub.created).format("YYYY MMM DD")} - {sub.canceled_at && moment.unix(sub.canceled_at).format("YYYY MMM DD")}
            </div>
          })
          .value()
      }
    </div>
      */}
    </div>
  );
}
